import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { http, HttpError } from '@/lib/http';
import { useIsMobile } from '../hooks/use-is-mobile';
import { useUiStore } from '../stores/ui';
import peerManager, { PeerAuthError } from '@/lib/peer-manager';
import { useSessionStore } from '@/stores/session';

const AUTH_TOKEN_KEY = 'mobile_auth_token';

export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  const isMobile = useIsMobile();
  const closeSidebar = useUiStore((state) => state.closeSidebar);

  useEffect(() => {
    const checkAuth = async () => {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        setIsAuthenticated(true);
        return;
      }
      const PROXY_DOMAIN = (import.meta as any).env.VITE_PROXY_DOMAIN;
      const isTrustedZone = (PROXY_DOMAIN && window.location.hostname.endsWith(PROXY_DOMAIN));
      if (isTrustedZone) {
        // Direct HTTP auth for trusted zones (like proxy domain)
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
          try {
            // Verify the token against a status endpoint.
            await http('/api/auth/status', { sendProjectId: false });
            setIsAuthenticated(true);
          } catch (error: any) {
            console.error("Auth token verification failed:", error);
            // Only remove the token for auth-related errors (401, 403), not network failures.
            if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
              localStorage.removeItem(AUTH_TOKEN_KEY); // Remove invalid token
            }
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } else {
        // WebRTC auth for P2P zones (static sites)
        useSessionStore.getState().setCommunicationMode('webrtc');
        const token = localStorage.getItem('webrtc_token');
        let peerId = localStorage.getItem('webrtc_peer_id');

        if (token && !peerId) {
          // If we have a token but no peerId, try to derive it from the token.
          // This helps if localStorage was partially cleared or from older versions.
          try {
            const payloadBase64 = token.split('.')[1];
            const decodedJson = atob(payloadBase64);
            const decodedPayload = JSON.parse(decodedJson);
            peerId = decodedPayload.aud; // 'aud' claim holds the peerId
            if (peerId) localStorage.setItem('webrtc_peer_id', peerId);
          } catch (e) {
            console.error("Failed to decode peerId from JWT, clearing token.", e);
            localStorage.removeItem('webrtc_token');
          }
        }

        if (token && peerId) {
          try {
            // Check if already connected to this peer to avoid unnecessary reconnection
            if (peerManager.isConnectedToPeer(peerId)) {
              setIsAuthenticated(true);
            } else {
              await peerManager.connectWithPersistentId(peerId, token);
              setIsAuthenticated(true);
            }
          } catch (error: any) {
            console.error("WebRTC reconnection failed:", error);
            if (error instanceof PeerAuthError) {
              localStorage.removeItem('webrtc_token');
              localStorage.removeItem('webrtc_peer_id');
            }
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();
  }, [location.key]); // Re-check on navigation

  useEffect(() => {
    if (isMobile) {
      closeSidebar();
    }
  }, [location.pathname, isMobile, closeSidebar]);

  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="ml-2">Checking authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/pages/onboarding/mobile-auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};